import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter } from "../../../../resources/constants";
import { connect } from "react-redux";
import SearchSelect from "../../../common/select/SearchSelect";

function InstallmentByFaculty() {
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [semesterList, setSemesterList] = useState([]);
    const [summaryStats, setSummaryStats] = useState({ TotalPlans: 0, ActiveCount: 0, CompletedCount: 0, DefaultedCount: 0 });
    const [filters, setFilters] = useState({ SemesterCode: "" });
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "Faculty", field: "FacultyName" },
            { label: "Total Plans", field: "TotalPlans" },
            { label: "Active", field: "ActiveCount" },
            { label: "Completed", field: "CompletedCount" },
            { label: "Defaulted", field: "DefaultedCount" },
            { label: "Total Expected", field: "TotalExpected" },
            { label: "Total Paid", field: "TotalPaid" },
            { label: "Remaining", field: "TotalRemaining" },
        ],
        rows: [],
    });

    const semesterOptions = useMemo(() => semesterList.map(s => ({
        value: s.SemesterCode,
        label: (s.SemesterName || s.SemesterCode) + (s.IsCurrent === 1 ? " (Current)" : ""),
    })), [semesterList]);

    const getSemesters = async () => {
        const result = await api.get("staff/registration/semester/list");
        if (result.success && result.data) {
            const data = Array.isArray(result.data) ? result.data : result.data.data || [];
            setSemesterList(data);
            const current = data.find(s => s.IsCurrent === 1);
            setFilters(f => ({ ...f, SemesterCode: current?.SemesterCode || data[0]?.SemesterCode || "" }));
        }
    };

    const getReportData = async () => {
        if (!filters.SemesterCode) { setIsLoading(false); return; }
        setIsLoading(true);
        const result = await api.get(`staff/finance/invoices/installment-by-faculty?semester=${filters.SemesterCode}`);
        if (result.success && result.data?.data) {
            const { summary, rows } = result.data.data;
            setSummaryStats(summary || {});
            setReportData(rows || []);
            buildTable(rows || []);
        }
        setIsLoading(false);
    };

    const buildTable = (data) => {
        const rows = data.map((item) => ({
            FacultyName: <span className="fw-bold">{item.FacultyName}</span>,
            TotalPlans: item.TotalPlans,
            ActiveCount: <span className="badge badge-light-info">{item.ActiveCount}</span>,
            CompletedCount: <span className="badge badge-light-success">{item.CompletedCount}</span>,
            DefaultedCount: <span className="badge badge-light-danger">{item.DefaultedCount}</span>,
            TotalExpected: <span>{currencyConverter(item.TotalExpected)}</span>,
            TotalPaid: <span className="text-success fw-bold">{currencyConverter(item.TotalPaid)}</span>,
            TotalRemaining: <span className="text-danger fw-bold">{currencyConverter(item.TotalRemaining)}</span>,
        }));
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        const headers = ["Faculty", "Total Plans", "Active", "Completed", "Defaulted", "Total Expected", "Total Paid", "Remaining"];
        const rows = reportData.map(item => [
            item.FacultyName, item.TotalPlans, item.ActiveCount, item.CompletedCount,
            item.DefaultedCount, item.TotalExpected, item.TotalPaid, item.TotalRemaining,
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `installment_by_faculty_${filters.SemesterCode}_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    useEffect(() => { getSemesters(); }, []);
    useEffect(() => { if (filters.SemesterCode) getReportData(); }, [filters.SemesterCode]);

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="Installment Plans by Faculty" items={["Finance", "Invoices", "Installment by Faculty"]} />
            <div className="flex-column-fluid">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label required">Semester</label>
                                <SearchSelect
                                    id="SemesterCode"
                                    value={semesterOptions.find(o => o.value === filters.SemesterCode) || null}
                                    options={semesterOptions}
                                    onChange={(s) => setFilters(f => ({ ...f, SemesterCode: s?.value || "" }))}
                                    placeholder="Select Semester"
                                    isClearable={false}
                                />
                            </div>
                            <div className="col-md-7 text-end">
                                <button className="btn btn-light-primary" onClick={exportToCSV} disabled={!reportData.length}>
                                    <i className="fa fa-download me-2"></i>Export CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {filters.SemesterCode && (
                    <>
                        <div className="row g-4 mb-4">
                            {[
                                { label: "Total Plans", value: summaryStats.TotalPlans, color: "primary", icon: "fa-list-ol", isCount: true },
                                { label: "Active", value: summaryStats.ActiveCount, color: "info", icon: "fa-spinner", isCount: true },
                                { label: "Defaulted", value: summaryStats.DefaultedCount, color: "danger", icon: "fa-exclamation-triangle", isCount: true },
                                { label: "Completed", value: summaryStats.CompletedCount, color: "success", icon: "fa-check-circle", isCount: true },
                            ].map((c, i) => (
                                <div className="col-md-3" key={i}>
                                    <div className={`card bg-light-${c.color}`}>
                                        <div className="card-body py-4">
                                            <div className="d-flex align-items-center">
                                                <div className="symbol symbol-50px me-3">
                                                    <span className={`symbol-label bg-${c.color}`}>
                                                        <i className={`fa ${c.icon} text-white fs-3`}></i>
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="fs-2 fw-bold">{c.value || 0}</div>
                                                    <div className="text-muted small">{c.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="card">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Faculty Installment Breakdown</h3>
                                <div className="card-toolbar">
                                    <span className="text-muted fs-7">{reportData.length} faculties</span>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                {reportData.length > 0 ? <AGTable data={datatable} /> : (
                                    <div className="text-center py-10"><p className="text-muted">No installment plans for this semester</p></div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default connect()(InstallmentByFaculty);
